import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Typography, Button, useStateInRef } from 'seagull';
import { useLocaleKeys } from '~/i18n';
import { SuccessBox, ErrorBox, ImportCard } from './CommonComponents';
import styles from './ImportWizard.module.scss';
import { createRowErrorsObject } from './ImportWizard.utils';
export default function UploadStep(props) {
    var _Object_keys;
    const { t: importWizardTexts } = useLocaleKeys('components.importWizard');
    const { importBtnText, renderUploadedSummary, csvTemplateUrl, testId, onStartImportItems, onEvent } = props;
    const getStateInRef = useStateInRef(props);
    const csvInputRef = useRef(null);
    const [errors, setErrors] = useState(null);
    const [csvFile, setCsvFile] = useState(null);
    const [parsedItems, setParsedItems] = useState([]);
    const [showCsvParseResults, setShowCsvParseResults] = useState(false);
    const [isInvalidTemplate, setIsInvalidTemplate] = useState(false);
    const isCsvValid = ()=>csvFile && !(errors || isInvalidTemplate) && (parsedItems === null || parsedItems === void 0 ? void 0 : parsedItems.length) > 0;
    useEffect(()=>{
        setIsInvalidTemplate(false);
        if (!csvFile) {
            setErrors(null);
            return;
        }
        const reader = new FileReader();
        reader.onload = async (e)=>{
            const { parseAndValidateCsv, onUploadCsv } = getStateInRef();
            try {
                var _e_target;
                const data = (_e_target = e.target) === null || _e_target === void 0 ? void 0 : _e_target.result;
                if (!data) return;
                const { items, errors } = await parseAndValidateCsv(data);
                onUploadCsv(items, errors);
                setErrors(createRowErrorsObject(errors));
                setParsedItems(items);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                onUploadCsv([], [], true);
                setIsInvalidTemplate(true);
                setErrors(null);
                setParsedItems([]);
            }
            setShowCsvParseResults(true);
        };
        reader.readAsText(csvFile);
    }, [
        csvFile
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.root,
        children: [
            csvTemplateUrl && /*#__PURE__*/ _jsx(ImportCard, {
                title: importWizardTexts.uploadTitle(),
                subtitle: importWizardTexts.uploadSubtitle(),
                actionsStart: /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'download_import_csv_template'
                    },
                    href: csvTemplateUrl,
                    as: "a",
                    onClick: ()=>onEvent && onEvent('csvTemplateDownload'),
                    size: "small",
                    testId: testId && testId + '-download-example',
                    download: (()=>{
                        const arr = csvTemplateUrl.split('/');
                        return arr[arr.length - 1];
                    })(),
                    children: importWizardTexts.downloadTemplate()
                })
            }),
            /*#__PURE__*/ _jsx(ImportCard, {
                title: csvTemplateUrl ? importWizardTexts.uploadYourDataNumbered() : importWizardTexts.uploadYourData(),
                subtitle: importWizardTexts.enterDataSubtitle(),
                actionsStart: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("input", {
                            type: "file",
                            accept: ".csv",
                            className: styles.uploadCsvInput,
                            value: "",
                            "data-testid": testId && testId + '-upload-csv-input',
                            ref: csvInputRef,
                            onChange: (e)=>{
                                var _files_length;
                                var _e_target;
                                const files = (_e_target = e.target) === null || _e_target === void 0 ? void 0 : _e_target.files;
                                if (!files) {
                                    return;
                                }
                                if ((_files_length = files.length) !== null && _files_length !== void 0 ? _files_length : 0 > 0) {
                                    const file = files[0];
                                    setCsvFile(file);
                                }
                            }
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'select_import_csv_file'
                            },
                            size: "small",
                            disabled: !!csvFile,
                            testId: testId && testId + '-upload-csv-btn',
                            onClick: ()=>csvInputRef.current && csvInputRef.current.click(),
                            children: importWizardTexts.uploadDescription()
                        }),
                        csvFile && /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            children: csvFile.name
                        })
                    ]
                })
            }),
            showCsvParseResults && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(ImportCard, {
                        title: importWizardTexts.resultsTitle(),
                        children: isCsvValid() ? /*#__PURE__*/ _jsx(SuccessBox, {
                            testId: testId && testId + '-upload-success',
                            children: importWizardTexts.fileUploadedSuccessfully({
                                summary: renderUploadedSummary(parsedItems)
                            })
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    children: importWizardTexts.fileUploadedWithErrors()
                                }),
                                /*#__PURE__*/ _jsxs(ErrorBox, {
                                    className: styles.parseResults,
                                    testId: testId && testId + '-parse-results',
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            className: styles.uploadErrorSummary,
                                            children: isInvalidTemplate ? importWizardTexts.invalidTemplateError() : importWizardTexts.foundErrors({
                                                count: (_Object_keys = Object.keys(errors || {})) === null || _Object_keys === void 0 ? void 0 : _Object_keys.length
                                            })
                                        }),
                                        errors && Object.keys(errors).map((rowNumber, index)=>/*#__PURE__*/ _jsxs("div", {
                                                className: styles.errorRow,
                                                "data-testid": testId && testId + "-csv-error-row-".concat(rowNumber),
                                                children: [
                                                    /*#__PURE__*/ _jsx(Typography, {
                                                        weight: "normal",
                                                        children: importWizardTexts.row({
                                                            rowNumber
                                                        })
                                                    }),
                                                    errors[rowNumber].map((errorMsg, idx)=>/*#__PURE__*/ _jsx("div", {
                                                            className: styles.uploadErrorItemMessage,
                                                            children: /*#__PURE__*/ _jsx(Typography, {
                                                                dangerouslySetInnerHTML: {
                                                                    __html: errorMsg
                                                                }
                                                            })
                                                        }, "specific-error-".concat(idx)))
                                                ]
                                            }, "error-row-".concat(index)))
                                    ]
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.actionButtons,
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'reset_import_csv'
                                },
                                skin: "primaryOutline",
                                testId: testId && testId + '-reset',
                                onClick: ()=>{
                                    setShowCsvParseResults(false);
                                    setCsvFile(null);
                                },
                                children: importWizardTexts.reset()
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'start_import_items'
                                },
                                disabled: !isCsvValid(),
                                testId: testId && testId + '-start-import',
                                onClick: ()=>onStartImportItems(parsedItems),
                                children: importBtnText
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
